/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngxs/store';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

import { getRemoteError } from '@app/core/errors/helpers/get-remote-error';
import { shouldRetry } from '@app/shared/rxjs/custom-operators';

import { RemoveStudentSessionAction } from '../../features/student/store/student-session/student-session.action';
import { StudentSessionState } from '../../features/student/store/student-session/student-session.state';
import {
  LivSuccessResponse,
  LivErrorResponse,
  LivResponseProtocol
} from '../models/response-protocol.model';
import { ToastService } from '../ui/components/toast';

const EXCLUDED_STATUS_CODES_ON_RETRY = [
  HttpStatusCode.NotFound,
  HttpStatusCode.Unauthorized,
  HttpStatusCode.UnprocessableEntity
];

const retryConfig = {
  maxRetryAttempts: 1,
  scalingDuration: 3000,
  excludedStatusCodes: EXCLUDED_STATUS_CODES_ON_RETRY
} as const;

@Injectable()
export class ResponseProtocolInterceptor implements HttpInterceptor {
  private readonly toast = inject(ToastService);
  private readonly store = inject(Store);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<LivSuccessResponse | LivErrorResponse>> {
    return next.handle(req).pipe(
      map((event) => this.handleResponseEvent(event)),
      retry(shouldRetry(retryConfig)),
      catchError((err: HttpErrorResponse) => this.handleError(err))
    );
  }

  private handleResponseEvent(
    event: HttpEvent<unknown>
  ): HttpEvent<LivSuccessResponse | LivErrorResponse> {
    if (event instanceof HttpResponse) {
      return event.clone({ body: this.handleResponse(event) });
    }
    return event;
  }

  handleResponse(
    event: HttpResponse<unknown>
  ): LivSuccessResponse | LivErrorResponse {
    const response = event.body as LivResponseProtocol;

    if (
      event.url?.includes('assets') ||
      event.url?.includes('google') ||
      event.body instanceof Blob
    ) {
      return event.body as never;
    }

    if (response.error) {
      throw new HttpErrorResponse({
        status: response.status ?? event.status,
        error: response.error,
        url: event.url ?? ''
      });
    }

    if ('data' in response) {
      return {
        status: response.status ?? event.status,
        data: response.data,
        meta: response.meta
      };
    }

    throw new HttpErrorResponse({
      status: event.status,
      error: event.body,
      url: event.url ?? ''
    });
  }

  private handleError(response: HttpErrorResponse): Observable<never> {
    if (response.status === HttpStatusCode.Unauthorized) {
      return this.handleUnauthorizedError(response);
    }

    const livError = this.createLivErrorResponse(response);

    return throwError(() => livError);
  }

  private handleUnauthorizedError(
    response: HttpErrorResponse
  ): Observable<never> {
    const isAuthenticated = !!this.store.selectSnapshot(
      StudentSessionState.token
    );

    if (isAuthenticated) {
      this.store.dispatch(new RemoveStudentSessionAction());

      const { message } = getRemoteError(response, {
        defaultMessage: 'Sua sessão expirou, faça login novamente.'
      });

      this.toast.danger(message, {
        id: 'jwt-session-expired'
      });
    }

    return EMPTY;
  }

  private createLivErrorResponse(
    response: HttpErrorResponse
  ): LivErrorResponse {
    const remote = getRemoteError(response);

    return {
      status: response.status,
      error: {
        message: remote.message,
        severity: remote.severity,
        stack: {
          url: response.url ?? ''
        }
      }
    };
  }
}
